import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { api, externalApi } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import MessageTemplateService from '../../../../services/messageTemplateService';
import moment from 'moment-timezone';
import BasicInfoForm from '../../../../components/campaigns/BasicInfoForm';
import TestCampaignSection from '../../../../components/campaigns/TestCampaignSection';
import CampaignHeader from '../../../../components/campaigns/CampaignHeader';
import CampaignTabs from '../../../../components/campaigns/CampaignTabs';
import DripMessageConfig from '../../../../components/campaigns/drip/DripMessageConfig';
import DripScheduleConfig from '../../../../components/campaigns/drip/DripScheduleConfig';
import CommunicationEndpointsProvider from '../../../../components/campaigns/CommunicationEndpointsProvider';
import OptOutConfig from '../../../../components/campaigns/OptOutConfig';
import { serializeDripCampaign } from '../../../../utils/campaignSerializers';
import RetryConfigurationPanel from '../../../../components/campaigns/RetryConfigurationPanel';
import { formatRetryConfigForBackend } from '../../../../utils/retryStrategyUtils';

// Get list of timezones from moment-timezone and format them for display
const timezones = moment.tz.names().map(zone => {
  const offset = moment.tz(zone).format('Z');
  const label = `(UTC${offset}) ${zone.replace(/_/g, ' ')}`;
  return {
    value: zone,
    label
  };
});

const channelTypes = ['email', 'sms', 'voice', 'chat'];

// Channel configuration utilities
const getDefaultChannelConfig = (channel) => {
  const defaults = {
    email: {
      content: '',
      subject: '',
      from_endpoint: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      track_opens: true,
      track_clicks: true,
      attachments: []
    },
    sms: {
      content: '',
      from_endpoint: '',
      priority: 'normal',
      track_delivery: true,
      track_replies: true,
      media_urls: []
    },
    voice: {
      content: '',
      from_endpoint: '',
      platform: 'bland_ai',
      voice_id: 'alexa',
      voice_name: 'Alexa (Female)',
      temperature: 0.7,
      max_duration: 5,
      retry_attempts: 3,
      voicemail_message: '',
      voicemail_action: 'hangup',
      record_call: false,
      call_timeout: 60,
      platform_config: {
        task: '',
        pathway_id: '',
        pathway_version: 1,
        first_sentence: '',
        background_track: '',
        model: 'base',
        wait_for_greeting: false,
        block_interruptions: false,
        interruption_threshold: 100,
        sensitive_voicemail_detection: false,
        transfer_phone_number: '',
        transfer_list: {},
        language: 'en-US',
        voicemail_sms: {},
        dispatch_hours: {},
        ignore_button_press: false,
        timezone: 'America/New_York',
        request_data: {},
        tools: [],
        start_time: '',
        voicemail_action: {},
        retry: { wait: 300, voicemail_action: 'leave_message' },
        max_duration: 5,
        record: false,
        webhook: '',
        webhook_events: [],
        metadata: {},
        citation_schema_id: '',
        analysis_preset: 'default',
        available_tags: [],
        geospatial_dialing: 'disabled',
        precall_dtmf_sequence: '',
        dynamic_data: [],
        keywords: [],
        pronunciation_guide: [],
        local_dialing: false
      },
      webhook_config: {
        webhook: '',
        webhook_events: []
      },
      metadata: {}
    },
    chat: {
      content: '',
      from_endpoint: '',
      platform: 'whatsapp',
      priority: 'normal',
      track_delivery: true,
      track_read: true,
      media_urls: [],
      quick_replies: []
    }
  };
  return defaults[channel] || {};
};

const getUpdatedChannelConfig = (channel, currentConfig, updates) => {
  const defaultConfig = getDefaultChannelConfig(channel);
  return {
    ...defaultConfig,
    ...currentConfig,
    ...updates
  };
};

const getStepChannelConfig = (channel, campaignConfig, stepConfig = {}) => {
  // Start with campaign-level config as base
  const baseConfig = { ...campaignConfig };
  
  // Only override specific fields that are explicitly set in step config
  const overrides = {};
  Object.entries(stepConfig).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      overrides[key] = value;
    }
  });

  return {
    ...baseConfig,
    ...overrides
  };
};

const normalizeChannelConfigs = (formData) => {
  const normalized = { ...formData };
  const channels = ['email', 'sms', 'voice', 'chat'];
  
  // Normalize main channel config
  const campaignChannelConfig = getUpdatedChannelConfig(
    formData.channel,
    normalized[`${formData.channel}_config`] || {},
    {}
  );
  normalized[`${formData.channel}_config`] = campaignChannelConfig;

  // Normalize message step channel configs
  if (normalized.drip_schedule?.message_steps) {
    normalized.drip_schedule.message_steps = normalized.drip_schedule.message_steps.map(step => {
      const updatedStep = { ...step };
      
      // Get step config using campaign config as base
      updatedStep[`${formData.channel}_config`] = getStepChannelConfig(
        formData.channel,
        campaignChannelConfig,
        step[`${formData.channel}_config`]
      );

      // Set other channel configs to null
      channels.forEach(ch => {
        if (ch !== formData.channel) {
          updatedStep[`${ch}_config`] = null;
        }
      });

      return updatedStep;
    });
  }

  return normalized;
};

const prepareSubmissionPayload = (formData) => {
  const normalizedFormData = normalizeChannelConfigs(formData);
  
  // Remove all root-level channel configs for drip campaigns (only store at step level)
  const { 
    voice_config, 
    sms_config, 
    email_config, 
    chat_config, 
    ...dataWithoutRootChannelConfigs 
  } = normalizedFormData;
  
  // Use the new serializer for proper voice platform support
  const payload = serializeDripCampaign({
    ...dataWithoutRootChannelConfigs,
    start_date: normalizedFormData.start_date ? new Date(normalizedFormData.start_date).toISOString() : null,
    end_date: normalizedFormData.is_ongoing ? null : (normalizedFormData.end_date ? new Date(normalizedFormData.end_date).toISOString() : null)
  });

  // Add properly formatted retry configuration
  const retryConfig = formatRetryConfigForBackend(formData.retry_strategy_id, formData.max_retries);
  return {
    ...payload,
    ...retryConfig
  };
};

const EditDripCampaign = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const token = useSelector(selectCurrentToken);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [communicationEndpoints, setCommunicationEndpoints] = useState([]);
  const [channelSwitchDialogOpen, setChannelSwitchDialogOpen] = useState(false);
  const [pendingChannel, setPendingChannel] = useState(null);
  const [previousChannelState, setPreviousChannelState] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    account: '',
    crm_campaign: '',
    start_date: '',
    end_date: '',
    active: true,
    is_ongoing: false,
    campaign_type: 'drip',
    channel: 'email',
    template: '',
    content: '',
    status: 'draft',
    auto_enroll_new_leads: false,
    auto_enroll_filters: [],
    // Retry configuration
    retry_strategy_id: null,
    max_retries: null,
    email_config: {
      content: '',
      subject: '',
      from_endpoint: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      track_opens: true,
      track_clicks: true,
      attachments: []
    },
    sms_config: {
      content: '',
      from_endpoint: '',
      priority: 'normal',
      track_delivery: true,
      track_replies: true,
      media_urls: []
    },
    voice_config: {
      content: '',
      from_endpoint: '',
      voice: 'female',
      language: 'en-US',
      priority: 'normal',
      retry_attempts: 3,
      retry_delay: 300,
      record_call: true,
      call_timeout: 60,
      machine_detection: 'prefer_human'
    },
    chat_config: {
      content: '',
      from_endpoint: '',
      platform: 'whatsapp',
      priority: 'normal',
      track_delivery: true,
      track_read: true,
      media_urls: [],
      quick_replies: []
    },
    drip_schedule: {
      business_hours_only: true,
      start_time: '09:00',
      end_time: '17:00',
      exclude_weekends: true,
      timezone: 'UTC',
      message_steps: [
        {
          order: 1,
          delay_units: 24,
          delay_unit_type: 'hours',
          template: null,
          content: null
        }
      ]
    },
    lead_filters: [],
    enable_opt_out: false,
    initial_opt_out_notice: '',
    opt_out_message: ''
  });

  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null,
    selectedDripStep: 0
  });

  // Load campaign data on component mount
  useEffect(() => {
    const fetchCampaignData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [campaignResponse, accountsResponse, templatesResponse, statusResponse] = await Promise.all([
          api.get(`/acs/nurturing-campaigns/${campaignId}/`),
          api.get('/crm/accounts/'),
          MessageTemplateService.getTemplates({ channel: formData.channel }),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);

        const campaign = campaignResponse.data;
        
        // Get the from_endpoint from the first message step if available
        const firstStepConfig = campaign.drip_schedule?.message_steps?.[0]?.[`${campaign.channel}_config`];
        const fromEndpoint = firstStepConfig?.from_endpoint;
        const fromEndpointValue = firstStepConfig?.from_endpoint_value;
        
        const normalizedData = normalizeChannelConfigs({
          ...campaign,
          email_config: {
            ...getDefaultChannelConfig('email'),
            ...campaign.email_config
          },
          sms_config: {
            ...getDefaultChannelConfig('sms'),
            ...campaign.sms_config,
            from_endpoint: fromEndpoint || '',
            from_endpoint_value: fromEndpointValue || ''
          },
          voice_config: {
            ...getDefaultChannelConfig('voice'),
            ...campaign.voice_config
          },
          chat_config: {
            ...getDefaultChannelConfig('chat'),
            ...campaign.chat_config
          },
          template: campaign.template || '',
          content: campaign.content || '',
          start_date: campaign.start_date ? new Date(campaign.start_date).toISOString().split('T')[0] : '',
          end_date: campaign.end_date ? new Date(campaign.end_date).toISOString().split('T')[0] : '',
          drip_schedule: {
            ...formData.drip_schedule,
            ...campaign.drip_schedule,
            message_steps: campaign.drip_schedule?.message_steps?.length > 0 
              ? campaign.drip_schedule.message_steps.map(step => {
                  return {
                    ...step,
                    // Ensure each step has the proper channel config with from_endpoint_value
                    [`${campaign.channel}_config`]: {
                      ...step[`${campaign.channel}_config`],
                      from_endpoint: fromEndpoint || '',
                      from_endpoint_value: fromEndpointValue || ''
                    }
                  };
                })
              : formData.drip_schedule.message_steps
          },
        });
        
        setFormData(normalizedData);

        setAccounts(accountsResponse.data);
        setTemplates(templatesResponse);
        setStatusChoices(statusResponse.data);

        // Load CRM campaigns for the selected account
        if (campaign.account) {
          const crmCampaignsResponse = await api.get(`/crm/accounts/${campaign.account}/campaigns/`);
          setCrmCampaigns(crmCampaignsResponse.data);
        }

        setDataLoaded(true);
      } catch (err) {
        console.error('Error loading campaign data:', err);
        setError('Failed to load campaign data');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaignData();
  }, [campaignId]);

  // Add effect to reload templates when channel changes
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templatesResponse = await MessageTemplateService.getTemplates({ channel: formData.channel });
        setTemplates(templatesResponse);
      } catch (err) {
        console.error('Error loading templates:', err);
      }
    };
    loadTemplates();
  }, [formData.channel]);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  const handleChannelChange = (newChannel) => {
    if (newChannel === formData.channel) return;

    // If there are existing message steps, show confirmation dialog
    if (formData.drip_schedule.message_steps.length > 0) {
      // Store current state before switching
      setPreviousChannelState({
        channel: formData.channel,
        channelConfig: formData[`${formData.channel}_config`],
        messageSteps: formData.drip_schedule.message_steps
      });
      setPendingChannel(newChannel);
      setChannelSwitchDialogOpen(true);
    } else {
      // If no message steps exist, switch channel directly
      switchChannel(newChannel);
    }
  };

  const switchChannel = (newChannel) => {
    // Reset channel-specific configs
    const newFormData = {
      ...formData,
      channel: newChannel,
      template: '', // Reset template selection
    };

    // Reset all channel configs
    ['email', 'sms', 'voice', 'chat'].forEach(channel => {
      newFormData[`${channel}_config`] = getDefaultChannelConfig(channel);
    });

    // Reset message steps to default
    newFormData.drip_schedule = {
      ...formData.drip_schedule,
      message_steps: [
        {
          order: 1,
          delay_units: 24,
          delay_unit_type: 'hours',
          template: null,
          content: null,
          [`${newChannel}_config`]: getDefaultChannelConfig(newChannel)
        }
      ]
    };

    setFormData(normalizeChannelConfigs(newFormData));
    setChannelSwitchDialogOpen(false);
    setPendingChannel(null);
  };

  const revertChannelSwitch = () => {
    if (previousChannelState) {
      const revertedFormData = {
        ...formData,
        channel: previousChannelState.channel,
        [`${previousChannelState.channel}_config`]: previousChannelState.channelConfig,
        drip_schedule: {
          ...formData.drip_schedule,
          message_steps: previousChannelState.messageSteps
        }
      };

      setFormData(normalizeChannelConfigs(revertedFormData));
      setPreviousChannelState(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;

    // Handle channel change specially
    if (name === 'channel') {
      handleChannelChange(value);
      return;
    }

    setFormData(prev => {
      const newData = { ...prev };
      if (name.includes('.')) {
        const [config, field] = name.split('.');
        newData[config] = {
          ...newData[config],
          [field]: inputValue
        };
      } else {
        newData[name] = inputValue;
      }
      return normalizeChannelConfigs(newData);
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = prepareSubmissionPayload(formData);
      await api.put(`/acs/drip-campaigns/${campaignId}/`, payload);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post(`/acs/nurturing-campaigns/${campaignId}/activate/`);
      setFormData(prev => ({ ...prev, status: 'active' }));
      setActivateDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to activate campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleTestCampaign = async () => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      const selectedStep = formData.drip_schedule.message_steps[testData.selectedDripStep];
      const channelConfig = selectedStep[`${formData.channel}_config`] || formData[`${formData.channel}_config`];
      
      if (formData.channel === 'voice') {
        // Handle voice testing - use step-level voice_config
        const stepVoiceConfig = selectedStep.voice_config || channelConfig;
        if (!stepVoiceConfig?.from_endpoint_value) {
          throw new Error('From endpoint is required');
        }

        const testPayload = {
          platform: stepVoiceConfig.platform || 'bland_ai',
          phone_number: testData.phone,
          from_endpoint: stepVoiceConfig.from_endpoint_value,
          // Platform-specific test data
          ...getPlatformTestPayload(stepVoiceConfig)
        };
        
        const response = await externalApi.post(`/communications/test-voice/${channelConfig.platform}/`, testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test voice call');
        }
      } else {
        // Handle other channels (SMS, email, chat)
        const message = selectedStep.template ? 
          templates.find(t => t.id === selectedStep.template)?.content : 
          selectedStep[`${formData.channel}_config`]?.content;

        if (!message) {
          throw new Error('Message content is required');
        }

        if (!channelConfig?.from_endpoint_value) {
          throw new Error('From endpoint is required');
        }

        const testPayload = {
          phone_number: testData.phone,
          message: message,
          twilio_number: channelConfig.from_endpoint_value
        };
        
        const response = await externalApi.post('/communications/send-direct-sms/', testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test message');
        }
      }
    } catch (err) {
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.detail || err.message || 'Failed to send test message'
      }));
    }
  };

  const getPlatformTestPayload = (channelConfig) => {
    const platform = channelConfig.platform || 'bland_ai';
    
    switch (platform) {
      case 'bland_ai':
        return {
          task: channelConfig.platform_config?.task || '',
          pathway_id: channelConfig.platform_config?.pathway_id || '',
          voice_id: channelConfig.voice_id || 'alexa',
          temperature: channelConfig.temperature || 0.7,
          max_duration: channelConfig.max_duration || 5
        };
      case 'vapi':
        return {
          voice_id: channelConfig.voice_id || 'shimmer',
          temperature: channelConfig.temperature || 0.7,
          max_duration: channelConfig.max_duration || 10
        };
      case 'elevenlabs':
        return {
          voice_id: channelConfig.voice_id || '21m00Tcm4TlvDq8ikWAM',
          temperature: channelConfig.temperature || 0.7,
          max_duration: channelConfig.max_duration || 5
        };
      case 'twilio':
        return {
          voice_id: channelConfig.voice_id || 'alexa'
        };
      default:
        return {};
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderMessageConfig = () => {
    return (
      <CommunicationEndpointsProvider
        accountId={formData.account}
        campaignId={formData.crm_campaign}
        channel={formData.channel}
        activeTab={activeTab}
      >
        {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
          <Grid container spacing={3}>
            {endpointsError && (
              <Grid item xs={12}>
                <Alert severity="error">{endpointsError}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <DripMessageConfig
                messageSteps={formData.drip_schedule.message_steps}
                onMessageStepsChange={(steps) => {
                  setFormData(prev => ({
                    ...prev,
                    drip_schedule: {
                      ...prev.drip_schedule,
                      message_steps: steps
                    }
                  }));
                }}
                template={formData.template}
                content={formData[`${formData.channel}_config`].content}
                onTemplateChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    template: value,
                    [`${prev.channel}_config`]: {
                      ...prev[`${prev.channel}_config`],
                      content: value ? '' : prev[`${prev.channel}_config`].content
                    }
                  }));
                }}
                onContentChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    template: '',
                    [`${prev.channel}_config`]: {
                      ...prev[`${prev.channel}_config`],
                      content: value
                    }
                  }));
                }}
                templates={templates}
                channel={formData.channel}
                channelConfig={formData[`${formData.channel}_config`]}
                onChannelConfigChange={(config) => {
                  setFormData(prev => ({
                    ...prev,
                    [`${prev.channel}_config`]: config
                  }));
                }}
                communicationEndpoints={endpoints}
                onEndpointChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    [`${prev.channel}_config`]: {
                      ...prev[`${prev.channel}_config`],
                      from_endpoint: value
                    }
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <OptOutConfig
                config={{
                  enable_opt_out: formData.enable_opt_out,
                  initial_opt_out_notice: formData.initial_opt_out_notice,
                  opt_out_message: formData.opt_out_message
                }}
                onChange={(updatedConfig) => {
                  setFormData(prev => ({
                    ...prev,
                    ...updatedConfig
                  }));
                }}
              />
            </Grid>
          </Grid>
        )}
      </CommunicationEndpointsProvider>
    );
  };

  const renderScheduleSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <DripScheduleConfig
          schedule={formData.drip_schedule}
          onScheduleChange={(schedule) => {
            setFormData(prev => ({
              ...prev,
              drip_schedule: {
                ...prev.drip_schedule,
                ...schedule
              }
            }));
          }}
          timezones={timezones}
          timezoneComponent={
            <Autocomplete
              options={timezones}
              getOptionLabel={(option) => option.label}
              value={timezones.find(tz => tz.value === formData.drip_schedule.timezone) || null}
              onChange={(_, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  drip_schedule: {
                    ...prev.drip_schedule,
                    timezone: newValue?.value || 'UTC'
                  }
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Timezone"
                  required
                  fullWidth
                  error={!formData.drip_schedule.timezone}
                  helperText={!formData.drip_schedule.timezone ? 'Timezone is required' : ''}
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              groupBy={(option) => option.value.split('/')[0]}
              renderGroup={(params) => (
                <li key={params.group}>
                  <Box sx={{ 
                    p: 1, 
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {params.group}
                    </Typography>
                  </Box>
                  <ul style={{ padding: 0, margin: 0 }}>
                    {params.children}
                  </ul>
                </li>
              )}
            />
          }
        />
      </Grid>
      
      {/* Retry Configuration */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mt: 2 }}>
          <RetryConfigurationPanel
            retryStrategy={formData.retry_strategy_id}
            maxRetries={formData.max_retries}
            onRetryStrategyChange={(strategyId) => setFormData(prev => ({ ...prev, retry_strategy_id: strategyId }))}
            onMaxRetriesChange={(maxRetries) => setFormData(prev => ({ ...prev, max_retries: maxRetries }))}
            description="Configure retry behavior for all drip messages (campaign-level default)"
          />
        </Paper>
      </Grid>
    </Grid>
  );

  if (loading && !dataLoaded) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <CampaignHeader
          title="Edit Drip Campaign"
          onBack={() => navigate('/crm/lead-nurturing')}
          onSave={handleSubmit}
          loading={loading}
          status={formData.status}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Campaign updated successfully
          </Alert>
        )}

        <CampaignTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          campaignType="drip"
          isEdit={true}
        />

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && (
            <BasicInfoForm
              formData={formData}
              onInputChange={handleInputChange}
              accounts={accounts}
              crmCampaigns={crmCampaigns}
              campaignTypes={{ drip: 'Drip Campaign' }}
            />
          )}
          {activeTab === 1 && renderMessageConfig()}
          {activeTab === 2 && renderScheduleSettings()}
          {activeTab === 3 && (
            <TestCampaignSection
              formData={formData}
              testData={testData}
              onTestDataChange={setTestData}
              onTestCampaign={handleTestCampaign}
              templates={templates}
            />
          )}
        </Box>

        {formData.status === 'draft' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              onClick={() => setActivateDialogOpen(true)}
              disabled={loading}
            >
              Activate Campaign
            </Button>
          </Box>
        )}
      </Paper>

      {/* Activate Campaign Dialog */}
      <Dialog
        open={activateDialogOpen}
        onClose={() => setActivateDialogOpen(false)}
      >
        <DialogTitle>Activate Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to activate this campaign? Once activated, the campaign will start sending messages according to the configured schedule.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Channel Switch Confirmation Dialog */}
      <Dialog
        open={channelSwitchDialogOpen}
        onClose={() => {
          setChannelSwitchDialogOpen(false);
          setPendingChannel(null);
        }}
      >
        <DialogTitle>Switch Channel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching channels will reset all message steps and channel-specific configurations.
            This action cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setChannelSwitchDialogOpen(false);
              setPendingChannel(null);
            }}
          >
            Cancel
          </Button>
          {previousChannelState && (
            <Button 
              onClick={revertChannelSwitch}
              color="secondary"
            >
              Revert to Previous Channel
            </Button>
          )}
          <Button 
            onClick={() => switchChannel(pendingChannel)} 
            color="warning" 
            variant="contained"
          >
            Switch Channel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditDripCampaign; 